import React, { useRef, useState, useEffect } from 'react';
import {
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Send, Mic } from '@mui/icons-material';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { defaultColorPallete } from 'utils';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow_down.svg';

type DropdownOption = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
};

type Props = {
  text: string;
  onSend: () => void;
  onMic?: () => void;
  icon?: React.ReactNode;
  tooltip?: string;
  dropdownOptions?: DropdownOption[];
  onArrowDown?: () => void;
};

export const AudioOrSendButton: React.FC<Props> = ({
  text,
  onSend,
  onMic,
  icon,
  tooltip,
  dropdownOptions = [],
  onArrowDown,
}) => {
  const controls = useAnimation();
  const isTexting = text.trim().length > 0;
  const [arrowVisible, setArrowVisible] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const arrowButtonRef = useRef<HTMLButtonElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  useEffect(() => {
    controls.start({
      x: isTexting ? -40 : 0,
      transition: { type: 'spring', stiffness: 260, damping: 20 },
    });

    if (isTexting) setArrowVisible(true);
    else setArrowVisible(false);
  }, [isTexting, controls]);

  const handleAnimationComplete = () => {
    if (!isTexting) setArrowVisible(false);
  };

  const handleMainClick = () => {
    if (icon || isTexting) return onSend();
    onMic?.();
  };

  const handleArrowClick = (event: React.MouseEvent<HTMLElement>) => {
    if (dropdownOptions.length > 0) {
      setMenuAnchorEl(event.currentTarget);
      return;
    }
    onArrowDown?.();
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: 72,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatePresence>
        {arrowVisible && (
          <motion.div
            initial={{
              opacity: 0,
              scaleX: 0.6,
              borderTopLeftRadius: 20,
              borderBottomLeftRadius: 20,
            }}
            animate={{
              opacity: 1,
              scaleX: 1,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            exit={{
              opacity: 0,
              scaleX: 0.6,
              borderTopLeftRadius: 20,
              borderBottomLeftRadius: 20,
            }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 0,
              transformOrigin: 'right center',
            }}
          >
            <IconButton
              ref={arrowButtonRef}
              onClick={handleArrowClick}
              sx={{
                width: 52,
                height: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                position: 'relative',
                bgcolor: defaultColorPallete.primary.dark,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: '20px',
                borderBottomRightRadius: '20px',
                '&:hover': {
                  bgcolor: defaultColorPallete.primary.main,
                },
              }}
            >
              <ArrowDownIcon />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        animate={controls}
        onAnimationComplete={handleAnimationComplete}
        style={{
          position: 'absolute',
          right: 0,
          zIndex: 1,
        }}
      >
        <Tooltip title={tooltip}>
          <IconButton
            onClick={handleMainClick}
            sx={{
              width: 56,
              height: 56,
              bgcolor: defaultColorPallete.primary.main,
              '&:hover': {
                bgcolor: defaultColorPallete.primary.light,
              },
            }}
          >
            {icon || (isTexting ? <Send sx={{ color: 'white' }} /> : <Mic sx={{ color: 'white' }} />)}
          </IconButton>
        </Tooltip>
      </motion.div>

      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {dropdownOptions.map((option, index) => (
          <MenuItem
            key={index}
            disabled={option.disabled}
            onClick={() => {
              option.onClick();
              handleCloseMenu();
            }}
          >
            {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
